import React from 'react';
import { Box, type BoxProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

export const APP_VIEWPORT_SX = {
  display: 'flex',
  minWidth: 0,
  minHeight: '100dvh',
  overflow: 'hidden',
  bgcolor: 'background.default',
  '@supports not (height: 100dvh)': {
    minHeight: '100vh',
  },
} satisfies SxProps<Theme>;

export const APP_MAIN_SX = {
  flexGrow: 1,
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  bgcolor: 'background.default',
} satisfies SxProps<Theme>;

const SHELL_PADDING_SX: SxProps<Theme> = {
  pt: {
    xs: '16px',
    sm: '24px',
    md: '24px',
  },
  pr: {
    xs: '16px',
    sm: '24px',
    md: '32px',
  },
  pb: {
    xs: '16px',
    sm: '24px',
    md: '24px',
  },
  pl: {
    xs: '16px',
    sm: '24px',
    md: '32px',
  },
};

const PAGE_PADDING_SX: SxProps<Theme> = {
  pt: {
    xs: 'max(env(safe-area-inset-top, 0px), 16px)',
    sm: 'max(env(safe-area-inset-top, 0px), 24px)',
    md: 'max(env(safe-area-inset-top, 0px), 24px)',
  },
  pr: {
    xs: 'max(env(safe-area-inset-right, 0px), 16px)',
    sm: 'max(env(safe-area-inset-right, 0px), 24px)',
    md: 'max(env(safe-area-inset-right, 0px), 32px)',
  },
  pb: {
    xs: 'max(env(safe-area-inset-bottom, 0px), 16px)',
    sm: 'max(env(safe-area-inset-bottom, 0px), 24px)',
    md: 'max(env(safe-area-inset-bottom, 0px), 24px)',
  },
  pl: {
    xs: 'max(env(safe-area-inset-left, 0px), 16px)',
    sm: 'max(env(safe-area-inset-left, 0px), 24px)',
    md: 'max(env(safe-area-inset-left, 0px), 32px)',
  },
};

const EDGE_SAFE_AREA_SX: SxProps<Theme> = {
  pt: 'env(safe-area-inset-top, 0px)',
  pr: 'env(safe-area-inset-right, 0px)',
  pb: 'env(safe-area-inset-bottom, 0px)',
  pl: 'env(safe-area-inset-left, 0px)',
};

type PagePaddingMode = 'page' | 'edge' | 'shell' | 'none';

export interface PageLayoutProps extends BoxProps {
  fillHeight?: boolean;
  contentMaxWidth?: number | string | false;
  paddingMode?: PagePaddingMode;
  innerSx?: SxProps<Theme>;
}

export const PageLayout: React.FC<PageLayoutProps> = ({
  children,
  fillHeight = false,
  contentMaxWidth = false,
  paddingMode = 'page',
  innerSx,
  sx,
  ...boxProps
}) => (
  <Box
    {...boxProps}
    sx={[
      {
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      },
      fillHeight
        ? {
            flex: 1,
            minHeight: 0,
          }
        : {},
      paddingMode === 'edge'
        ? EDGE_SAFE_AREA_SX
        : paddingMode === 'shell'
          ? SHELL_PADDING_SX
          : paddingMode === 'none'
            ? {}
            : PAGE_PADDING_SX,
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    <Box
      sx={[
        {
          width: '100%',
          minWidth: 0,
          mx: contentMaxWidth === false ? 0 : 'auto',
          display: 'flex',
          flexDirection: 'column',
        },
        fillHeight
          ? {
              flex: 1,
              minHeight: 0,
            }
          : {},
        contentMaxWidth === false ? {} : { maxWidth: contentMaxWidth },
        ...(Array.isArray(innerSx) ? innerSx : innerSx ? [innerSx] : []),
      ]}
    >
      {children}
    </Box>
  </Box>
);
